import { useAuth } from '@contexts/auth-provider/hooks/use-auth/useAuth';
import { useModules } from '@contexts/modules-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { ProfileContextValue, ProfileProviderProps } from './ProfileProvider.types';

import { fetchProfile } from '../api/fetch-profile/fetchProfile';
import { PROFILE_GC_TIME_MS, PROFILE_STALE_TIME_MS } from './ProfileProvider.constants';

const ProfileContext = React.createContext<
  ProfileContextValue | undefined
>(undefined);

const ProfileProvider = React.memo(
  ({ children }: Readonly<ProfileProviderProps>): React.ReactElement => {
    const { isAuthenticated } = useAuth();
    const { selectedModule } = useModules();
    const { selectedOrganization } = useOrganizations();
    const organizationId = selectedOrganization?.id ?? '';
    const requiresOrganization = selectedModule?.name === 'Manage';

    const {
      data: profile,
      isLoading: isProfileLoading,
      isError: isProfileError,
      error: profileError,
    } = useQuery({
      queryKey: ['client-profiles', organizationId],
      queryFn: () => fetchProfile(organizationId),
      enabled:
        isAuthenticated && (!requiresOrganization || !!organizationId),
      staleTime: PROFILE_STALE_TIME_MS,
      gcTime: PROFILE_GC_TIME_MS,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    });

    const value: ProfileContextValue = React.useMemo(
      () => ({
        profile,
        isProfileLoading,
        isProfileError,
        profileError,
      }),
      [
        profile,
        isProfileLoading,
        isProfileError,
        profileError,
      ]
    );

    return (
      <ProfileContext value={value}>
        {children}
      </ProfileContext>
    );
  }
);

ProfileProvider.displayName = 'ProfileProvider';

export { ProfileProvider, ProfileContext };
